import type { HireAgentResponsibilities } from "~/lib/onboarding/hire-agent-preferences";
import {
  type MarketplaceHirePlanId,
  type MarketplaceSchedulingIntegrationId,
  getHirePlan,
} from "~/lib/marketplace/hire-pricing";

/** Defaults applied on the Agent Details step when cloning a marketplace template. */
export type HireTemplateDefaults = {
  responsibilities?: HireAgentResponsibilities;
  agentName?: string;
  jobTitle?: string;
  profileImageUrl?: string;
};

export function responsibilitiesFromTemplateChannels(
  channels: Record<string, unknown> | null | undefined,
): HireAgentResponsibilities {
  const c = channels ?? {};
  return {
    liveChat: c.aiChat !== false,
    textMessages: c.sms === true,
    email: c.email === true,
    browserCalls: c.talk === true,
    phoneCalls: c.call === true,
    videoCalls: c.talk === true,
    welcomeVideo: false,
    messenger: c.messenger === true,
    whatsapp: c.whatsapp === true,
    teamHandoff: c.chatTeamHandoffDelegateEnabled !== false,
    appointmentsScheduling: c.chatAppointmentsEnabled === true,
  };
}

/** Map plan tier + scheduling choice → Agent Details responsibility toggles. */
export function responsibilitiesFromHirePlan(
  planId: MarketplaceHirePlanId,
  schedulingIntegrationId: MarketplaceSchedulingIntegrationId,
): HireAgentResponsibilities {
  const plan = getHirePlan(planId);
  return {
    liveChat: plan.skills.includes("Live Chat"),
    textMessages: plan.skills.includes("Text Messages"),
    email: plan.skills.includes("Email"),
    browserCalls:
      plan.skills.includes("Browser Calls") || plan.skills.includes("Voice (WebRTC)"),
    phoneCalls: plan.skills.includes("Phone Calls"),
    videoCalls: plan.skills.includes("Video Calls"),
    welcomeVideo: plan.skills.includes("Welcome Video"),
    messenger: plan.skills.includes("Messenger"),
    whatsapp: plan.skills.includes("WhatsApp"),
    teamHandoff: true,
    appointmentsScheduling: schedulingIntegrationId !== "none",
  };
}

/** Intersect plan limits with cloned template channels for Agent Details defaults. */
export function mergeHireResponsibilities(
  fromPlan: HireAgentResponsibilities,
  fromTemplate?: HireAgentResponsibilities,
): HireAgentResponsibilities {
  if (!fromTemplate) return fromPlan;
  return {
    liveChat: fromPlan.liveChat && fromTemplate.liveChat,
    textMessages: fromPlan.textMessages && fromTemplate.textMessages,
    email: fromPlan.email && fromTemplate.email,
    browserCalls: fromPlan.browserCalls && fromTemplate.browserCalls,
    phoneCalls: fromPlan.phoneCalls && fromTemplate.phoneCalls,
    videoCalls: fromPlan.videoCalls && fromTemplate.videoCalls,
    welcomeVideo: fromPlan.welcomeVideo && fromTemplate.welcomeVideo,
    messenger: fromPlan.messenger && fromTemplate.messenger,
    whatsapp: fromPlan.whatsapp && fromTemplate.whatsapp,
    teamHandoff: fromPlan.teamHandoff && fromTemplate.teamHandoff,
    appointmentsScheduling:
      fromPlan.appointmentsScheduling || fromTemplate.appointmentsScheduling,
  };
}

export const HIRE_AGENT_DETAILS_CHECKLIST = [
  "Agent name and profile photo",
  "Job title for your business",
  "Live Chat, Phone, Video, and scheduling responsibilities",
] as const;
